import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ContentItem } from "@/types/content";
import { formatCount, typeLabels } from "@/lib/content";

export default function HeroCarousel({ items }: { items: ContentItem[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[560px] md:h-[640px] overflow-hidden">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - ${typeLabels[item.type]}`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 via-foreground-950/45 to-foreground-950/20" />

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-2xl">
          <div key={active.id} className="animate-xx-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
              <i className="ri-fire-line" />
              今日精选 · {typeLabels[active.type]}
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 mt-5 leading-tight">
              {active.title}
            </h1>
            <p className="text-background-50/85 text-sm md:text-base mt-4 leading-relaxed max-w-xl line-clamp-3">
              {active.summary}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Link
                to={`/detail/${active.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg" />
                立即观看
              </Link>
              <Link
                to={`/category/${active.type}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-medium hover:bg-background-50/25 transition-colors whitespace-nowrap border border-background-50/30"
              >
                浏览更多{typeLabels[active.type]}
              </Link>
            </div>
            <div className="flex items-center gap-5 mt-6 text-background-50/80 text-xs">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" /> {formatCount(active.views)} 次浏览
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-3-line" /> {formatCount(active.likes)} 点赞
              </span>
              <span className="hidden sm:flex items-center gap-1">
                <i className="ri-user-line" /> {active.author}
              </span>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一个"
        onClick={() => go(-1)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background-50/20 hover:bg-background-50/35 backdrop-blur text-background-50 flex items-center justify-center transition-colors"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一个"
        onClick={() => go(1)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background-50/20 hover:bg-background-50/35 backdrop-blur text-background-50 flex items-center justify-center transition-colors"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 条`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-8 bg-primary-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}